import { router } from 'expo-router';
import { useAtomValue, useSetAtom } from 'jotai';
import { useMemo, useState } from 'react';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import {
    BodyMediumEmphasized,
    BodyMediumRegular,
    CONTENT_MAX_WIDTH,
    InfoButton,
    isWeb,
    ScreenContainer,
    ScrollView,
    Spinner,
    View,
    XStack,
    YStack,
} from 'smoothie';

import { formatDollars } from '../../../common/utils/bankingUtils';
import { convertCategoryToIcon } from '../../../common/utils/billUtils';
import LogRocketFacade from '../../../logger/log-rocket/LogRocketFacade';
import { OpenBillsAdminSheetButton } from '../../admin/BillsAdminSheet';
import {
    activeAndReviewBillsAtom,
    allBillsAtom,
    selectedBillAtom,
    selectedBillCategoriesAtom,
} from '../../state/BillsAtoms';
import {
    BillActivationStatus,
    BillCategory,
    BillCategoryBackgroundColors,
    BillCategoryPrecedence,
    BillCreationType,
    type IBill,
    type PartialBill,
} from '../../types/Bill';
import BillFilterPills from './BillFilterPills';
import { BillList } from './BillList';
import { BillHeaderBubbles } from './BillsHeader';
import FilterSheet from './FilterSheet';

function compareCategory(a: IBill, b: IBill): number {
    return (
        BillCategoryPrecedence[a.category] - BillCategoryPrecedence[b.category]
    );
}

function compareAmount(a: IBill, b: IBill): number {
    return b.amount - a.amount;
}

function transformBills(arr: IBill[]): IBill[] {
    // sorting logic:
    // 1. bills in review
    // 2. bills with proposed changes
    // 3. sorted by category
    // 4. sorted by amount DESC
    return arr
        .sort((a, b) => {
            if (
                a.activationStatus === BillActivationStatus.REVIEW &&
                b.activationStatus !== BillActivationStatus.REVIEW
            ) {
                return -1;
            } else if (
                b.activationStatus === BillActivationStatus.REVIEW &&
                a.activationStatus !== BillActivationStatus.REVIEW
            ) {
                return 1;
            } else if (a.proposedChanges && !b.proposedChanges) {
                return -1;
            } else if (b.proposedChanges && !a.proposedChanges) {
                return 1;
            }

            const categoryComparison = compareCategory(a, b);
            if (categoryComparison !== 0) {
                return categoryComparison;
            }

            return compareAmount(a, b);
        })
        .filter((bill) => {
            // Exclude only Quiltt (AUTO) credit card minimum bills
            // Show manual and spinwheel credit card minimum bills
            if (bill.category === BillCategory.CREDIT_CARD_MINIMUM) {
                return bill.creationType !== BillCreationType.AUTO;
            }
            return true;
        })
        .map((bill) => {
            const { category } = bill;
            return {
                ...bill,
                icon: convertCategoryToIcon(category),
                color: BillCategoryBackgroundColors[category],
            };
        });
}

const BillsHome = () => {
    LogRocketFacade.tagPage('BillsOverview');

    const { bottom } = useSafeAreaInsets();

    const setSelectedBill = useSetAtom(selectedBillAtom);
    const selectedBillCategories = useAtomValue(selectedBillCategoriesAtom);

    const [isFilterSheetOpen, setIsFilterSheetOpen] = useState(false);

    const allBills = useAtomValue(allBillsAtom);
    const activeAndReviewBills = useAtomValue(activeAndReviewBillsAtom);
    const sortedBillList = transformBills([
        ...allBills.filter((bill) =>
            [BillActivationStatus.REVIEW, BillActivationStatus.ACTIVE].includes(
                bill.activationStatus,
            ),
        ),
    ]);

    const showBillDetails = (bill: IBill | PartialBill) => {
        setSelectedBill(bill);
        router.navigate('/bills/bill-details-new');
    };

    const allBillCategories = useMemo(
        () => Array.from(new Set(sortedBillList.map((bill) => bill.category))),
        [sortedBillList],
    );

    const activeAndReviewBillsCount = activeAndReviewBills.filter((bill) =>
        selectedBillCategories.length
            ? selectedBillCategories.includes(bill.category)
            : true,
    ).length;

    const billsMonthlyTotal = formatDollars(
        sortedBillList
            .filter((bill) =>
                selectedBillCategories.length
                    ? selectedBillCategories.includes(bill.category)
                    : true,
            )
            .reduce((acc, bill) => acc + (bill.monthlyAmount ?? 0), 0),
        false,
    );

    return (
        <ScreenContainer
            title="Bills"
            hasBackButton={true}
            onFallbackRoute="/"
            rightAction={
                <>
                    <InfoButton
                        onPress={() => {
                            router.navigate('/bills/bills-category-info');
                        }}
                    />
                    <OpenBillsAdminSheetButton />
                </>
            }
            scrollContainerOverride={(children) =>
                sortedBillList.length < 1 ? (
                    <Spinner marginVertical="auto" />
                ) : (
                    <ScrollView
                        flex={1}
                        stickyHeaderIndices={[1]}
                        showsVerticalScrollIndicator={isWeb}
                        contentContainerStyle={{
                            paddingBottom: bottom,
                            maxWidth: CONTENT_MAX_WIDTH,
                            alignSelf: isWeb ? 'center' : 'auto',
                        }}
                    >
                        {children}
                    </ScrollView>
                )
            }
        >
            <BillHeaderBubbles bills={sortedBillList} />
            <YStack gap={10} padding={20} backgroundColor="$white">
                <XStack
                    padding={isWeb ? 20 : 0}
                    alignItems="center"
                    justifyContent="space-between"
                >
                    <YStack>
                        <BodyMediumEmphasized>
                            {activeAndReviewBillsCount}
                        </BodyMediumEmphasized>
                        <BodyMediumRegular>Total bills</BodyMediumRegular>
                    </YStack>
                    <YStack
                        alignItems="flex-end"
                        justifyContent="space-between"
                    >
                        <BodyMediumEmphasized>
                            {billsMonthlyTotal}
                        </BodyMediumEmphasized>
                        <BodyMediumRegular>Monthly average</BodyMediumRegular>
                    </YStack>
                </XStack>
                <BillFilterPills billCategories={allBillCategories} />
            </YStack>
            <View paddingHorizontal={20} gap={10}>
                {sortedBillList.length === 0 ? (
                    <Spinner />
                ) : (
                    <BillList
                        bills={sortedBillList.filter(
                            (bill) =>
                                selectedBillCategories.length === 0 ||
                                selectedBillCategories.includes(bill.category),
                        )}
                        showBillDetails={showBillDetails}
                    />
                )}
            </View>
            {isFilterSheetOpen && (
                <FilterSheet
                    allBillCategories={allBillCategories}
                    onClose={() => {
                        setIsFilterSheetOpen(false);
                    }}
                />
            )}
        </ScreenContainer>
    );
};

export default BillsHome;
